import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  DealIntelligenceGap,
  GapCommentsApiResponse,
  GapFeedbackVote,
  GetDealIntelligenceApiResponse,
  SubmitGapFeedbackApiResponse,
} from '@momentum/types';

import { gapService } from '../services/gap.service';

const DEAL_INTELLIGENCE_QUERY_KEY = ['deal-intelligence'];

export const useDealIntelligence = () => {
  return useQuery({
    queryKey: DEAL_INTELLIGENCE_QUERY_KEY,
    queryFn: () => gapService.getDealIntelligence(),
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Applies `updateGap` to the one gap matching `gapId` within the cached
 * library response, leaving every other project/gap untouched.
 */
function patchCachedGap(
  cachedResponse: GetDealIntelligenceApiResponse | undefined,
  gapId: string,
  updateGap: (gap: DealIntelligenceGap) => DealIntelligenceGap
): GetDealIntelligenceApiResponse | undefined {
  if (!cachedResponse) return cachedResponse;
  return {
    ...cachedResponse,
    projects: cachedResponse.projects.map((project) => ({
      ...project,
      gaps: project.gaps.map((projectGap) =>
        projectGap._id === gapId ? updateGap(projectGap) : projectGap
      ),
    })),
  };
}

/** Patches one gap's comments in the cached library response. */
function patchCachedComments(
  cachedResponse: GetDealIntelligenceApiResponse | undefined,
  commentsResult: GapCommentsApiResponse
): GetDealIntelligenceApiResponse | undefined {
  return patchCachedGap(cachedResponse, commentsResult.gapId, (gap) => ({
    ...gap,
    comments: commentsResult.comments,
  }));
}

/** Add a comment to a gap; the cached library is patched in place. */
export const useAddGapComment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ gapId, text }: { gapId: string; text: string }) =>
      gapService.addGapComment(gapId, { text }),
    onSuccess: (commentsResult: GapCommentsApiResponse) => {
      queryClient.setQueryData<GetDealIntelligenceApiResponse>(
        DEAL_INTELLIGENCE_QUERY_KEY,
        (cachedResponse) => patchCachedComments(cachedResponse, commentsResult)
      );
    },
  });
};

/** Delete the caller's own comment; the cached library is patched in place. */
export const useDeleteGapComment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ gapId, commentId }: { gapId: string; commentId: string }) =>
      gapService.deleteGapComment(gapId, commentId),
    onSuccess: (commentsResult: GapCommentsApiResponse) => {
      queryClient.setQueryData<GetDealIntelligenceApiResponse>(
        DEAL_INTELLIGENCE_QUERY_KEY,
        (cachedResponse) => patchCachedComments(cachedResponse, commentsResult)
      );
    },
  });
};

/**
 * Set or clear the caller's relevance vote on a gap. The vote is applied
 * optimistically - `myVote` flips in the cache on click and rolls back if
 * the request fails; on success the server's counts are patched in too.
 */
export const useGapFeedback = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      gapId,
      vote,
    }: {
      gapId: string;
      vote: GapFeedbackVote | null;
    }) => gapService.submitGapFeedback(gapId, { vote }),
    onMutate: async ({ gapId, vote }) => {
      await queryClient.cancelQueries({
        queryKey: DEAL_INTELLIGENCE_QUERY_KEY,
      });
      const previousResponse =
        queryClient.getQueryData<GetDealIntelligenceApiResponse>(
          DEAL_INTELLIGENCE_QUERY_KEY
        );
      queryClient.setQueryData<GetDealIntelligenceApiResponse>(
        DEAL_INTELLIGENCE_QUERY_KEY,
        (cachedResponse) =>
          patchCachedGap(cachedResponse, gapId, (gap) => ({
            ...gap,
            myVote: vote ?? undefined,
          }))
      );
      return { previousResponse };
    },
    onError: (_error, _variables, mutationContext) => {
      queryClient.setQueryData(
        DEAL_INTELLIGENCE_QUERY_KEY,
        mutationContext?.previousResponse
      );
    },
    onSuccess: (feedbackResult: SubmitGapFeedbackApiResponse) => {
      queryClient.setQueryData<GetDealIntelligenceApiResponse>(
        DEAL_INTELLIGENCE_QUERY_KEY,
        (cachedResponse) =>
          patchCachedGap(cachedResponse, feedbackResult.gapId, (gap) => ({
            ...gap,
            myVote: feedbackResult.myVote,
            voteCounts: feedbackResult.voteCounts,
          }))
      );
    },
  });
};
